import { Component, OnInit, ChangeDetectionStrategy } from '@angular/core';

import { FormBuilder, FormGroup, FormArray, Validators, ReactiveFormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { MatCardModule } from '@angular/material/card';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatChipsModule } from '@angular/material/chips';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatDividerModule } from '@angular/material/divider';
import { MatTooltipModule } from '@angular/material/tooltip';

import { ClientService } from '../../../core/services/client.service';
import { KeycloakClient } from '../../../core/models/client.model';

@Component({
  selector: 'app-add-client',
  imports: [
    ReactiveFormsModule,
    MatCardModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatCheckboxModule,
    MatButtonModule,
    MatIconModule,
    MatChipsModule,
    MatSnackBarModule,
    MatProgressSpinnerModule,
    MatDividerModule,
    MatTooltipModule
],
  templateUrl: './add-client.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./add-client.component.scss']
})
export class AddClientComponent implements OnInit {
  clientForm: FormGroup;
  isSubmitting = false;

  clientTypes = [
    { value: 'external-app', label: 'Client externe' },
    { value: 'internal-app', label: 'Client interne' },
    { value: 'neutral-app', label: 'Client neutre' }
  ];

  // Options pour les protocoles
  protocols = [
    { value: 'openid-connect', label: 'OpenID Connect' },
    { value: 'saml', label: 'SAML' }
  ];

  basicInfoExpanded = true;
  configExpanded = true;
  redirectsExpanded = true;
  originsExpanded = true;

  loading = false;

  constructor(
    private formBuilder: FormBuilder,
    private clientService: ClientService,
    private router: Router,
    private snackBar: MatSnackBar
  ) {
    this.clientForm = this.createForm();
  }

  ngOnInit(): void {
    this.setupFormSubscriptions();
  }

  /**
   * Crée le formulaire réactif
   */
  private createForm(): FormGroup {
    return this.formBuilder.group({
      // Informations de base
      clientId: ['', [Validators.required, Validators.pattern(/^[a-zA-Z0-9_-]+$/)]],
      name: [''],
      description: [''],
      type: [''],

      // URLs
      rootUrl: ['', [Validators.pattern(/^https?:\/\/.+/)]],
      baseUrl: ['', [Validators.pattern(/^https?:\/\/.+/)]],

      // Configuration de base
      enabled: [true],
      protocol: ['openid-connect', Validators.required],

      // Types de client (exclusifs)
      publicClient: [false],
      bearerOnly: [false],

      // Flows OAuth/OIDC
      standardFlowEnabled: [true],
      implicitFlowEnabled: [false],
      directAccessGrantsEnabled: [false],
      serviceAccountsEnabled: [false],

      // URLs multiples
      redirectUris: this.formBuilder.array([]),
      webOrigins: this.formBuilder.array([])
    });
  }

  /**
   * Configure les souscriptions du formulaire pour les validations conditionnelles
   */
  private setupFormSubscriptions(): void {
    // Gérer les types de clients mutuellement exclusifs
    this.clientForm.get('publicClient')?.valueChanges.subscribe(isPublic => {
      if (isPublic) {
        this.clientForm.patchValue({ bearerOnly: false }, { emitEvent: false });
        this.clientForm.get('serviceAccountsEnabled')?.setValue(false);
      }
    });

    this.clientForm.get('bearerOnly')?.valueChanges.subscribe(isBearerOnly => {
      if (isBearerOnly) {
        this.clientForm.patchValue({
          publicClient: false,
          standardFlowEnabled: false,
          implicitFlowEnabled: false,
          directAccessGrantsEnabled: false
        }, { emitEvent: false });
      }
    });

    // Les Service Accounts nécessitent un client confidentiel
    this.clientForm.get('serviceAccountsEnabled')?.valueChanges.subscribe(enabled => {
      if (enabled) {
        this.clientForm.patchValue({ publicClient: false }, { emitEvent: false });
      }
    });
  }

  /**
   * Getters pour les FormArrays
   */
  get redirectUris(): FormArray {
    return this.clientForm.get('redirectUris') as FormArray;
  }

  get webOrigins(): FormArray {
    return this.clientForm.get('webOrigins') as FormArray;
  }

  /**
   * Ajoute une URI de redirection
   */
  addRedirectUri(): void {
    const control = this.formBuilder.control('', [
      Validators.required,
      Validators.pattern(/^https?:\/\/.+/)
    ]);
    this.redirectUris.push(control);
  }

  /**
   * Supprime une URI de redirection
   */
  removeRedirectUri(index: number): void {
    this.redirectUris.removeAt(index);
  }

  /**
   * Ajoute une origine web
   */
  addWebOrigin(): void {
    const control = this.formBuilder.control('', [
      Validators.required,
      Validators.pattern(/^https?:\/\/.+/)
    ]);
    this.webOrigins.push(control);
  }

  /**
   * Supprime une origine web
   */
  removeWebOrigin(index: number): void {
    this.webOrigins.removeAt(index);
  }

  /**
   * Vérifie si le formulaire est valide
   */
  isFormValid(): boolean {
    return this.clientForm.valid;
  }

  /**
   * Obtient le message d'erreur pour un champ
   */
  getFieldError(fieldName: string): string {
    const field = this.clientForm.get(fieldName);
    if (field?.errors && field.touched) {
      if (field.errors['required']) {
        return `Le champ ${fieldName} est requis`;
      }
      if (field.errors['pattern']) {
        switch (fieldName) {
          case 'clientId':
            return 'L\'ID client ne peut contenir que des lettres, chiffres, tirets et underscores';
          case 'rootUrl':
          case 'baseUrl':
            return 'L\'URL doit commencer par http:// ou https://';
          default:
            return 'Format invalide';
        }
      }
    }
    return '';
  }

  /**
   * Génère automatiquement des valeurs basées sur le clientId
   */
  onClientIdChange(): void {
    const clientId = this.clientForm.get('clientId')?.value;
    if (clientId && !this.clientForm.get('name')?.value) {
      // Génère un nom lisible à partir du clientId
      const name = clientId
        .split(/[-_]/)
        .map((word: string) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
        .join(' ');
      this.clientForm.patchValue({ name });
    }
  }

  /**
   * Applique un template de configuration prédéfini
   */
  applyTemplate(template: string): void {
    switch (template) {
      case 'webapp':
        this.applyWebAppTemplate();
        break;
      case 'spa':
        this.applySpaTemplate();
        break;
      case 'mobile':
        this.applyMobileTemplate();
        break;
      case 'service':
        this.applyServiceTemplate();
        break;
      default:
        break;
    }
  }

  /**
   * Template pour application web traditionnelle
   */
  private applyWebAppTemplate(): void {
    this.clientForm.patchValue({
      publicClient: false,
      bearerOnly: false,
      standardFlowEnabled: true,
      implicitFlowEnabled: false,
      directAccessGrantsEnabled: false,
      serviceAccountsEnabled: false
    });
    this.showSuccessMessage('Template "Application Web" appliqué');
  }

  /**
   * Template pour Single Page Application
   */
  private applySpaTemplate(): void {
    this.clientForm.patchValue({
      publicClient: true,
      bearerOnly: false,
      standardFlowEnabled: true,
      implicitFlowEnabled: false,
      directAccessGrantsEnabled: false,
      serviceAccountsEnabled: false
    });
    this.showSuccessMessage('Template "Single Page App" appliqué');
  }

  /**
   * Template pour application mobile
   */
  private applyMobileTemplate(): void {
    this.clientForm.patchValue({
      publicClient: true,
      bearerOnly: false,
      standardFlowEnabled: true,
      implicitFlowEnabled: false,
      directAccessGrantsEnabled: true,
      serviceAccountsEnabled: false
    });
    this.showSuccessMessage('Template "Application Mobile" appliqué');
  }

  /**
   * Template pour service/API
   */
  private applyServiceTemplate(): void {
    this.clientForm.patchValue({
      publicClient: false,
      bearerOnly: false,
      standardFlowEnabled: false,
      implicitFlowEnabled: false,
      directAccessGrantsEnabled: false,
      serviceAccountsEnabled: true
    });
    this.showSuccessMessage('Template "Service/API" appliqué');
  }

  /**
   * Soumet le formulaire pour créer le client
   */
  onSubmit(): void {
    if (!this.isFormValid() || this.isSubmitting) {
      return;
    }

    this.isSubmitting = true;

    // Préparer les données du client
    const formValue = this.clientForm.value;
    const clientData: Partial<KeycloakClient> = {
      clientId: formValue.clientId,
      name: formValue.name || formValue.clientId,
      description: formValue.description,
      type: formValue.type,
      rootUrl: formValue.rootUrl,
      baseUrl: formValue.baseUrl,
      enabled: formValue.enabled,
      publicClient: formValue.publicClient,
      bearerOnly: formValue.bearerOnly,
      standardFlowEnabled: formValue.standardFlowEnabled,
      implicitFlowEnabled: formValue.implicitFlowEnabled,
      directAccessGrantsEnabled: formValue.directAccessGrantsEnabled,
      serviceAccountsEnabled: formValue.serviceAccountsEnabled,
      protocol: formValue.protocol,
      redirectUris: this.redirectUris.value.filter((uri: string) => uri.trim()),
      webOrigins: this.webOrigins.value.filter((origin: string) => origin.trim())
    };

    this.clientService.createClient(clientData)
      .subscribe({
        next: (createdClient: KeycloakClient) => {
          this.showSuccessMessage(`Client "${createdClient.name || createdClient.clientId}" créé avec succès`);
          this.router.navigate(['/dashboard/clients']);
        },
        error: (error) => {
          console.error('Erreur lors de la création du client:', error);
          this.showErrorMessage(`Erreur lors de la création: ${error.message}`);
          this.isSubmitting = false;
        }
      });
  }

  /**
   * Annule et retourne à la liste
   */
  onCancel(): void {
    if (this.clientForm.dirty) {
      const confirmed = confirm(
        'Vous avez des modifications non sauvegardées. Êtes-vous sûr de vouloir annuler ?'
      );
      if (!confirmed) {
        return;
      }
    }
    this.router.navigate(['/dashboard/clients']);
  }

  /**
   * Réinitialise le formulaire
   */
  resetForm(): void {
    const confirmed = confirm('Êtes-vous sûr de vouloir réinitialiser le formulaire ?');
    if (confirmed) {
      this.clientForm.reset();
      this.redirectUris.clear();
      this.webOrigins.clear();
      this.clientForm.patchValue({
        enabled: true,
        protocol: 'openid-connect',
        standardFlowEnabled: true
      });
      this.showSuccessMessage('Formulaire réinitialisé');
    }
  }

  /**
   * Affiche un message de succès
   */
  private showSuccessMessage(message: string): void {
    this.snackBar.open(message, 'Fermer', {
      duration: 4000,
      panelClass: ['success-snackbar'],
      horizontalPosition: 'end',
      verticalPosition: 'top'
    });
  }

  /**
   * Affiche un message d'erreur
   */
  private showErrorMessage(message: string): void {
    this.snackBar.open(message, 'Fermer', {
      duration: 6000,
      panelClass: ['error-snackbar'],
      horizontalPosition: 'end',
      verticalPosition: 'top'
    });
  }

  /**
   * Vérifie si un type de client est sélectionné
   */
  isClientTypeSelected(): boolean {
    return this.clientForm.get('publicClient')?.value ||
      this.clientForm.get('bearerOnly')?.value ||
      (!this.clientForm.get('publicClient')?.value && !this.clientForm.get('bearerOnly')?.value);
  }

  /**
   * Obtient le type de client actuel pour l'affichage
   */
  getCurrentClientType(): string {
    const formValue = this.clientForm.value;
    if (formValue.bearerOnly) return 'Bearer Only';
    if (formValue.publicClient) return 'Public';
    return 'Confidentiel';
  }
}